import React, { useState } from "react";
import { useSelector } from "react-redux";
import ViewTicketModal from "./ViewTicketModal";

// Helper function for dynamic styling of badges
const getBadgeColor = (module) => {
    if (module <= 3) {
        return "bg-red-100 text-red-700";
    } else if (module > 3 && module <= 8) {
        return "bg-yellow-100 text-yellow-700";
    } else if (module > 8) {
        return "bg-[#90c740]/10 text-[#90c740]";
    } else {
        return "bg-gray-100 text-gray-700";
    }
};
const SkeletonRow = ({ columns }) => (
    <tr className="border-b">
      {columns.map((_, index) => (
        <td key={index} className="px-6 py-4">
          <div className="h-4 bg-gray-300 rounded animate-pulse w-3/4"></div>
        </td>
      ))}
    </tr>
  );

const renderCellValue = (col, row, handleIconClick) => {
    if (col.type === "image") {
      return (
        <div className="flex items-center space-x-3">
          <img className="h-10 w-10 rounded-full object-cover" src={row[col.key]} alt={row.name} />
          <div className="min-w-0">
            <p className="truncate font-medium text-gray-900">{row.name}</p>
            <p className="truncate text-sm text-gray-500">{row.email}</p>
          </div>
        </div>
      );
    }

    if (col.type === "badge") {
      return (
        <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getBadgeColor(row.noOfModule)}`}>
          {row[col.key]}
        </span>
      );
    }

    if (col.type === "custom") {
      return <col.component onClick={() => handleIconClick(row)} companyId={row.id} />;
    }

    if (col.type === "checkBox") {
      return (
        <div className="flex items-center">
          <input type="checkbox" className="form-checkbox h-5 w-5 text-blue-600 opacity-50" checked={row[col.key]} readOnly />
        </div>
      );
    }

    if (col.type === "htmlPara") {
      return <div className="text-left" dangerouslySetInnerHTML={{ __html: row[col.key] }} />;
    }

    return row[col.key] ? <p className="text-gray-700 break-words">{row[col.key]}</p> : <p className="text-gray-500">-</p>;
};

  const DynamicTable = ({ columns, data, type = "default" }) => {
    const isLoading = useSelector((state) => state.company.isLoading);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
  
    // Filter for tickets type: Show only rows with referenceKey not null
    const filteredData = type === "tickets" ? data.filter(row => !row.reference) : data;
    const transformTicketsToModalData = (ticket) => {
      if (!ticket) return {};
    
      return {
        initiator: {
          type: "text",
          label: "Initiator",
          value: ticket.Initiator || "N/A",
        },
        supportMember: {
          type: "text",
          label: "Support Member",
          value: ticket.SupportMember || "N/A",
        },
        createdAt: {
          type: "date",
          label: "Created At",
          value: ticket.created,
        },
        updatedAt: {
          type: "date",
          label: "Updated At",
          value: ticket.updated,
        },
        communication: {
          type: "html",
          label: "Communication",
          value: ticket.Communication || "No communication available.",
        },
        reference: {
          type: "text",
          label: "Reference",
          value: ticket.reference || "N/A",
        }
      };
    };
    
    const handleIconClick = (ticket) => {
      // first check which ticket is clicked 
      console.log("ticket clicked " , ticket);
      const { id : selectedTicketId } = ticket;
      setSelectedTicket(selectedTicketId);
      console.log("transformTicketsToModalData(ticket)" , transformTicketsToModalData(ticket));
      // setSelectedTicket(transformTicketsToModalData(ticket));
      setIsModalOpen(true);
    };
  
    return (
      <>
        <div className="overflow-x-auto bg-white shadow-md rounded-lg border-2 mt-4">
          <div className="space-y-3 p-3 md:hidden">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="rounded-xl border border-gray-200 p-4 shadow-sm">
                  <div className="space-y-3">
                    {columns.map((col) => (
                      <div key={col.key}>
                        <div className="mb-1 h-3 w-24 animate-pulse rounded bg-gray-200"></div>
                        <div className="h-4 w-full animate-pulse rounded bg-gray-100"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : filteredData.length ? (
              filteredData.map((row, rowIndex) => (
                <div key={row.id || rowIndex} className="rounded-xl border border-gray-200 p-4 shadow-sm">
                  <div className="space-y-3">
                    {columns.map((col) => (
                      <div key={col.key} className="space-y-1">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{col.label}</p>
                        {renderCellValue(col, row, handleIconClick)}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-gray-500">
                No data available.
              </div>
            )}
          </div>

          <table className="hidden min-w-full border-collapse md:table">
            {/* Table Header */}
            <thead>
              <tr className="bg-gray-100 text-left text-gray-600">
                {columns.map((col) => (
                  <th key={col.key} className="px-6 py-3">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
  
            {/* Table Body */}
            <tbody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, index) => <SkeletonRow key={index} columns={columns} />)
                : filteredData.length
                ? filteredData.map((row, rowIndex) => (
                    <tr key={rowIndex} className="border-b hover:bg-gray-50">
                      {columns.map((col) => (
                        <td key={col.key} className={`px-6 py-4 ${col.type === "custom" && "relative"}`}>
                          {renderCellValue(col, row, handleIconClick)}
                        </td>
                      ))}
                    </tr>
                  ))
                : <tr className="border-b hover:bg-gray-50">
                    {columns.map((col) => (
                      <td className="px-6 py-4">
                        <p className="text-gray-700">-</p>
                      </td>
                    ))}
                  </tr>
              }
            </tbody>
          </table>
        </div>
      {isModalOpen && (
                  <ViewTicketModal
                    selectedTicket = {selectedTicket}
                    title="Ticket Details"
                    tickets={data}
                    onClose={() => setIsModalOpen(false)}
                  />
                )}
      </>
    );
  };

export default DynamicTable;
